import { useState } from "react";
import { AddNewProductModal } from "../../../../shared/addnewproduct/AddNewProductModal";
import { ProductListingTab } from "./ProductListingTab";
import { CategoryMappingTab } from "./CategoryMappingTab";
import { SaltMappingTab } from "./SaltMappingTab";
import { DomainMappingTab } from "./DomainMappingTab";
import { ProductFormTab } from "./ProductFormTab";

type SectionId =
  | "products"
  | "categoryMapping"
  | "saltMapping"
  | "domainMapping"
  | "productForm";

const SECTIONS: { id: SectionId; label: string; hint: string }[] = [
  {
    id: "products",
    label: "Products",
    hint: "Every product in the catalogue",
  },
  {
    id: "categoryMapping",
    label: "Category Mapping",
    hint: "Primary category, sub category and product group, and their combinations",
  },
  {
    id: "saltMapping",
    label: "Salt Mapping",
    hint: "Salts, drugs, their mapping and the medicine schedules",
  },
  {
    id: "domainMapping",
    label: "Domain Mapping",
    hint: "Domains, regulatory domains and the category rules that resolve them",
  },
  {
    id: "productForm",
    label: "Product Form",
    hint: "Dosage form groups and the forms inside them",
  },
];

/**
 * Admin products section.
 *
 * The product listing sits beside the masters it depends on, because a product
 * cannot be filed without them: a category mapping row, a salt, a domain rule
 * and a product form all have to exist before the product form can reference
 * them. Keeping them in one screen means an admin can add a missing master and
 * come straight back to the product.
 */
export function StockProductsView() {
  const [section, setSection] = useState<SectionId>("products");
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [listingRefreshToken, setListingRefreshToken] = useState(0);

  const closeProductForm = () => {
    setIsProductFormOpen(false);
    setEditingProductId(null);
  };

  // The product form takes over the whole view rather than opening in a dialog:
  // it is a multi-step form and needs the room.
  if (isProductFormOpen) {
    return (
      <AddNewProductModal
        productId={editingProductId || ""}
        onSuccess={() => setListingRefreshToken((token) => token + 1)}
        onClose={closeProductForm}
        categoryId={1}
      />
    );
  }

  const activeSection = SECTIONS.find((item) => item.id === section);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl p-2">
        <nav className="flex flex-wrap gap-2">
          {SECTIONS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSection(item.id)}
              title={item.hint}
              className={`px-5 py-2.5 rounded-md text-sm font-medium border transition-colors ${
                section === item.id
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>
        {activeSection && (
          <p className="text-xs text-gray-500 px-3 pt-2 pb-1">
            {activeSection.hint}
          </p>
        )}
      </div>

      {section === "products" && (
        <ProductListingTab
          refreshToken={listingRefreshToken}
          onOpenProduct={(productId) => {
            setEditingProductId(productId);
            setIsProductFormOpen(true);
          }}
        />
      )}
      {section === "categoryMapping" && <CategoryMappingTab />}
      {section === "saltMapping" && <SaltMappingTab />}
      {section === "domainMapping" && <DomainMappingTab />}
      {section === "productForm" && <ProductFormTab />}
    </div>
  );
}
